import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { INSPECTION_COMMENTS, SUB_STRUCTURE_UNIT_KEY, STRUCTURE_UNIT_TYPE } from "@/constants/Inspections";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import { clone } from "@/util/clone";
import { useInspectionStore } from "@/stores/inspection";
import { SORT_ORDER, TYPES } from "@/constants/CommonWebConstants";
import { CONFIG_SORT_BY } from "@/constants/EnvConfigProperties";
import { isStructureUnitIn500YrFp } from "@/composables/waterways.js";
import { useSubstructureStore } from "@/components/inspection/store/substructure";

let structure = null;

function createInspectionNotesSection(structureUnit, sectionTitle) {
  return [
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [
          [
            {
              text: sectionTitle,
              fillColor: "#ebebeb",
              alignment: "center",
              style: ["sectionHeader"],
            },
          ],
        ],
      },
      layout: "noBorders",
      margin: [0, 5, 0, 0],
    },
    { columns: [{ width: "25%", text: "Backwall: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.BACKWALL) }] },
    { columns: [{ width: "25%", text: "Bridge Seats: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.BRIDGE_SEATS) }] },
    { columns: [{ width: "25%", text: "Cheekwalls: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.CHEEKWALLS) }] },
    { columns: [{ width: "25%", text: "Stem: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.STEM) }] },
    { columns: [{ width: "25%", text: "Wings: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.WINGS) }] },
    { columns: [{ width: "25%", text: "Footing: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.FOOTING) }] },
    { columns: [{ width: "25%", text: "Piles: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.PILES) }] },
    { columns: [{ width: "25%", text: "Settlement: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.SETTLEMENT) }] },
    { columns: [{ width: "25%", text: "Embank Slope-Wall: ", bold: true, margin: [0, 5, 0, 0] }] },
    {
      columns: [
        { width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.EMBANK_SLOPE_WALL) },
      ],
    },
    { columns: [{ width: "25%", text: "Wall Drainage: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(structureUnit?.structureUnitId, INSPECTION_COMMENTS.WALL_DRAINAGE) }] },
  ];
}

function createPierInspectionNotesSection(pier) {
  return [
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [
          [
            {
              text: "Pier " + getPierNumber(pier) + " Inspection Notes",
              fillColor: "#ebebeb",
              alignment: "center",
              style: ["sectionHeader"],
            },
          ],
        ],
      },
      layout: "noBorders",
      margin: [0, 5, 0, 0],
    },
    { columns: [{ width: "25%", text: "Condition Summary: ", bold: true, margin: [0, 5, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(pier?.structureUnitId, INSPECTION_COMMENTS.CONDITION_SUMMARY_PIER) }] },
    { columns: [{ width: "25%", text: "Bridge Seats: ", bold: true, margin: [0, 3, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(pier?.structureUnitId, INSPECTION_COMMENTS.BRIDGE_SEATS_PIER) }] },
    { columns: [{ width: "25%", text: "Cheekwalls: ", bold: true, margin: [0, 3, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(pier?.structureUnitId, INSPECTION_COMMENTS.CHEEKWALLS_PIER) }] },
    { columns: [{ width: "25%", text: "Columns/Stems: ", bold: true, margin: [0, 3, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(pier?.structureUnitId, INSPECTION_COMMENTS.COLUMNS_STEMS_PIER) }] },
    { columns: [{ width: "25%", text: "Settlement: ", bold: true, margin: [0, 3, 0, 0] }] },
    { columns: [{ width: "*", text: getComment(pier?.structureUnitId, INSPECTION_COMMENTS.SETTLEMENT_PIER) }] },
  ];
}

function createWaterwaySection(
  sectionTitle,
  underwaterInsp,
  structureUnit,
  pierAndAbutTypes,
  inspectionStore,
  configStore,
) {
  return [
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [[{ text: sectionTitle, fillColor: "#ebebeb", alignment: "center", style: ["sectionHeader"] }]],
      },
      layout: "noBorders",
    },
    {
      columns: [
        { width: "26%", text: "WA.27 Sub Unit: ", bold: true, margin: [0, 5, 0, 0] },
        { width: "*", text: inspectionStore.getSubUnit(structureUnit?.strUnitKey), margin: [0, 5, 0, 0] },
        { width: "30%", text: "WA.40 Pier/Abutment Type: ", bold: true, margin: [0, 5, 0, 0] },
        {
          width: "*",
          text: pierAndAbutTypes?.find((type) => type?.value == underwaterInsp?.subUnitType)?.title,
          margin: [0, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.28 Current Inspection? ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: inspectionStore.getReportCheckbox(underwaterInsp?.previousCurrent),
          style: "customFontAwesome",
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.41 Inv Foundation Type: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.PA_FOUNDATION_TYPE, underwaterInsp?.invFoundType),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.29 SCBI Code: ", bold: true, margin: [0, 3, 0, 0] },
        { width: "*", text: underwaterInsp?.scbiCode, margin: [0, 3, 0, 0] },
        { width: "30%", text: "WA.42 OSA Foundation Type: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.OSA_FOUNDATION_TYPE, underwaterInsp?.foundType),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.30 SCBI Case: ", bold: true, margin: [0, 3, 0, 0] },
        { width: "*", text: underwaterInsp?.scbiCase, margin: [0, 3, 0, 0] },
        { width: "30%", text: "WA.43 Streambed Material: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.STREAM_BED_MATERIAL, underwaterInsp?.streamBedMaterial),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.31 Scour Rating: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.OBSERVED_SCOUR_RATING, underwaterInsp?.obsScourRating),
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.44 UW Inspection Type: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.UNDERWATER_INSPECTION_TYPE,
            underwaterInsp?.underwaterInspType,
          ),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.32 Change Since Last Insp: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.CHANGE_SINCE_LAST_INSP,
            underwaterInsp?.changeSinceLastInsp,
          ),
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.45 Observed Scour Depth: ", bold: true, margin: [0, 3, 0, 0] },
        { width: "*", text: underwaterInsp?.obsScourDepth, margin: [0, 3, 0, 0] },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.33 Scour Hole: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.SCOUR_HOLE, underwaterInsp?.scourHole),
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.46 Water Depth: ", bold: true, margin: [0, 3, 0, 0] },
        { width: "*", text: underwaterInsp?.maxWaterDepth, margin: [0, 3, 0, 0] },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.34 Debris Potential: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.DEBRIS_POTENTIAL, underwaterInsp?.debrisPotential),
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.47 Movement Indicator: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: inspectionStore.getReportCheckbox(underwaterInsp?.movement),
          style: "customFontAwesome",
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.35 Scourability: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.SUBSTRUCTURE_SCOURABILITY, underwaterInsp?.subScour),
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.48 Scour/Undermine Indicator: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: inspectionStore.getReportCheckbox(underwaterInsp?.scourUndermine),
          style: "customFontAwesome",
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.36 Opening Adeq Channel: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.OPENING_ADEQUACY_CHANNEL,
            underwaterInsp?.openAdeqChannel,
          ),
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.49 Countermeasures: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: inspectionStore.getReportCheckbox(underwaterInsp?.counterMeasure),
          style: "customFontAwesome",
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.37 Sediment: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.SEDIMENT_DEPOSITS, underwaterInsp?.sedDeposit),
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.50 100yr Flood Scour Depth: ", bold: true, margin: [0, 3, 0, 0] },
        { width: "*", text: underwaterInsp?.calcScourDepth100, margin: [0, 3, 0, 0] },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.38 Alignment: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.ALIGNMENT, underwaterInsp?.alignment),
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.51 500yr Flood Scour Depth: ", bold: true, margin: [0, 3, 0, 0] },
        { width: "*", text: underwaterInsp?.calcScourDepth500, margin: [0, 3, 0, 0] },
      ],
    },
    {
      columns: [
        { width: "26%", text: "WA.39 Velocity Stream Slope: ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.VELOCITY_STREAM_SLOPE, underwaterInsp?.veloStreamSlope),
          margin: [0, 3, 0, 0],
        },
        { width: "30%", text: "WA.52 In 500yr Flood Plain? ", bold: true, margin: [0, 3, 0, 0] },
        {
          width: "*",
          text: inspectionStore.getReportCheckbox(
            isStructureUnitIn500YrFp(structureUnit?.strUnitKey, structure.inspectionsUnderwater) ? "1" : "0",
          ),
          style: "customFontAwesome",
          margin: [0, 3, 0, 0],
        },
      ],
    },
    { columns: [{ width: "25%", text: "WA.53 Notes: ", bold: true, margin: [0, 3, 0, 0] }] },
    { columns: [{ width: "*", text: underwaterInsp?.underwaterInspDesc }] },
  ];
}

function createFatigueSection(sectionTitle, fatigueTable) {
  return [
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [[{ text: sectionTitle, fillColor: "#ebebeb", alignment: "center", style: ["sectionHeader"] }]],
      },
      layout: "noBorders",
    },
    {
      table: {
        headerRows: 1,
        widths: ["5%", "15%", "15%", "20%", "*", "*"],
        body: fatigueTable,
      },
      layout: {
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
    },
  ];
}

function createSectionHeader(title) {
  return {
    table: {
      headerRows: 0,
      widths: ["100%"],
      body: [[{ text: title, fillColor: "#ebebeb", alignment: "center", style: ["sectionHeader"] }]],
    },
    layout: "noBorders",
    margin: [0, 5, 0, 0],
  };
}

function setSubstructureDocDefinition(structureValue) {
  structure = structureValue;
  //common code
  const inspectionStore = useInspectionStore();
  const substructureStore = useSubstructureStore();
  const configStore = useConfigStore();
  const pierAndAbutTypes = configStore.getReferenceValues(
    REFERENCE_TABLE.PIER_ABUTMENT_TYPE,
    CONFIG_SORT_BY.VALUE,
    SORT_ORDER.ASC,
    false,
    TYPES.NUMBER,
  );
  //nearAbutmentCode
  const nearAbutmentSubstructureUnitKey = substructureStore.getSubStructureUnitKey(
    structure,
    SUB_STRUCTURE_UNIT_KEY.NEAR_ABUTMENT,
  );
  const nearAbutmentStructureUnit = getStructureUnit(nearAbutmentSubstructureUnitKey);
  const nearAbutmentUnderwaterInsp = getUnderwaterInsp(nearAbutmentSubstructureUnitKey);
  let nearAbutmentFatigueTable = [];

  nearAbutmentFatigueTable.push(nstmFatigueTable());

  const nearAbutmentFatigues = getNSTMFatigues(nearAbutmentSubstructureUnitKey);

  nearAbutmentFatigueTable = setupFatigueTables(
    nearAbutmentFatigues,
    nearAbutmentFatigueTable,
    configStore,
    findLocation,
  );
  //farAbutmentCode
  const farAbutmentSubstructureUnitKey = substructureStore.getSubStructureUnitKey(
    structure,
    SUB_STRUCTURE_UNIT_KEY.FAR_ABUTMENT,
  );
  const farAbutmentStructureUnit = getStructureUnit(farAbutmentSubstructureUnitKey);
  const farAbutmentUnderwaterInsp = getUnderwaterInsp(farAbutmentSubstructureUnitKey);
  let farAbutmentFatigueTable = [];

  farAbutmentFatigueTable.push(nstmFatigueTable());

  const farAbutmentFatigues = getNSTMFatigues(farAbutmentSubstructureUnitKey);

  farAbutmentFatigueTable = setupFatigueTables(farAbutmentFatigues, farAbutmentFatigueTable, configStore, findLocation);

  //pierCode
  function getPierFatigue(structureUnitKey) {
    let pierFatigueTable = [];

    pierFatigueTable.push(nstmFatigueTable());

    const pierFatigues = getNSTMFatigues(structureUnitKey);
    pierFatigueTable = setupFatigueTables(pierFatigues, pierFatigueTable, configStore, findLocation);
    return pierFatigueTable;
  }

  const docDefinition = {
    header: () => getReportHeader("Substructure Page", structure),
    footer: (currentPage, pageCount) => getReportFooter(currentPage, pageCount),
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Substructure Page" },
      createSectionHeader("Substructure Conditions"),
      {
        columns: [
          { width: "35%", text: "B.C.03 Substructure Condition Rating: ", bold: true, margin: [0, 5, 0, 0] },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.subRating),
            margin: [5, 5, 0, 0],
          },
        ],
      },
      { columns: [{ width: "35%", text: "Substructure Notes: ", bold: true, margin: [0, 3, 0, 0] }] },
      {
        columns: [
          {
            width: "*",
            text: structure?.inspectionComments?.find(
              (element) => element?.commentType == INSPECTION_COMMENTS.SUBSTRUCTURE,
            )?.notes,
          },
        ],
      },
      createSectionHeader("Near Abutment"),
      ...createInspectionNotesSection(nearAbutmentStructureUnit, "Near Abutment Inspection Notes"),
    ],
    styles: styles,
    defaultStyle: { fontSize: 10 },
  };
  if (isStructureUnitIn500YrFp(nearAbutmentStructureUnit?.strUnitKey, structure.inspectionsUnderwater)) {
    docDefinition.content.push(
      ...createWaterwaySection(
        "Near Abutment - Waterway",
        nearAbutmentUnderwaterInsp,
        nearAbutmentStructureUnit,
        pierAndAbutTypes,
        inspectionStore,
        configStore,
      ),
    );
  }
  if (nearAbutmentFatigues?.length > 0) {
    docDefinition.content.push(...createFatigueSection("Near Abutment - NSTM/Fatigue", nearAbutmentFatigueTable));
  }
  docDefinition.content.push(
    createSectionHeader("Far Abutment"),
    ...createInspectionNotesSection(farAbutmentStructureUnit, "Far Abutment Inspection Notes"),
  );
  if (isStructureUnitIn500YrFp(farAbutmentStructureUnit?.strUnitKey, structure.inspectionsUnderwater)) {
    docDefinition.content.push(
      ...createWaterwaySection(
        "Far Abutment - Waterway",
        farAbutmentUnderwaterInsp,
        farAbutmentStructureUnit,
        pierAndAbutTypes,
        inspectionStore,
        configStore,
      ),
    );
  }
  if (farAbutmentFatigues?.length > 0) {
    docDefinition.content.push(...createFatigueSection("Far Abutment - NSTM/Fatigue", farAbutmentFatigueTable));
  }
  docDefinition.content.push(createSectionHeader("Piers"));
  let floodPlains = getFloodPlains(inspectionStore);
  let piers = getPiers();
  for (let pier of piers ?? []) {
    let floodPlain = floodPlains?.find((a) => a?.strUnitKey == pier?.strUnitKey);
    docDefinition.content.push(
      createSectionHeader("Pier " + getPierNumber(pier)),
      ...createPierInspectionNotesSection(pier),
    );
    if (isStructureUnitIn500YrFp(pier?.strUnitKey, structure.inspectionsUnderwater)) {
      docDefinition.content.push(
        ...createWaterwaySection(
          "Pier " + getPierNumber(pier) + " - Waterway",
          floodPlain,
          { strUnitKey: floodPlain?.strUnitKey },
          pierAndAbutTypes,
          inspectionStore,
          configStore,
        ),
      );
    }
    if (getNSTMFatigues(pier?.strUnitKey)?.length > 0) {
      docDefinition.content.push(
        ...createFatigueSection("Pier " + getPierNumber(pier) + " - NSTM/Fatigue", getPierFatigue(pier?.strUnitKey)),
      );
    }
  }

  return docDefinition;
}
function getLocations() {
  const inspectionStore = useInspectionStore();
  return inspectionStore
    .getFilteredStructureUnits([STRUCTURE_UNIT_TYPE.ABUTMENT, STRUCTURE_UNIT_TYPE.PIER], true)
    ?.map((b) => ({
      title: `${b?.strUnitKey} - ${b?.strUnitLabel}`,
      value: b?.strUnitKey,
    }))
    .sort((a, b) => a?.value - b?.value);
}
function findLocation(value) {
  const locations = getLocations();
  return locations?.find((a) => a.value === value)?.title;
}
function getStructureUnit(value) {
  return structure?.structureUnits?.find((elementToFind) => elementToFind?.strUnitKey == value);
}
function getUnderwaterInsp(value) {
  return structure?.inspectionsUnderwater?.find((element) => element?.strUnitKey == value);
}
function getNSTMFatigues(value) {
  return structure?.inspectionFCs?.filter((a) => a?.strUnitKey === value);
}
function getComment(structureUnitId, commentType) {
  return structure?.structureUnitInspComments?.find(
    (element) => element?.structureUnitId == structureUnitId && element?.commentType == commentType,
  )?.notes;
}

function setupFatigueTables(fatigues, table, configStore, findLocation) {
  for (const fatigue of fatigues ?? []) {
    if (fatigue) {
      table.push(
        [
          {
            text: fatigue?.fcKey,
            style: ["tableBody"],
          },
          {
            text: findLocation(fatigue?.strUnitKey),
            style: ["tableBody"],
          },
          {
            text: configStore.getReferenceValue(REFERENCE_TABLE.FRACTURE_CRITICAL_MEMBER_TYPE, fatigue?.fcMemType),
            style: ["tableBody"],
          },
          {
            text: fatigue?.fcDetail,
            style: ["tableBody"],
          },
          {
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.FRACTURE_CRITICAL_FATIGUE_STRESS_CATEGORY,
              fatigue?.fatigStressCat,
              true,
            ),
            style: ["tableBody"],
          },
          {
            text: fatigue?.nstmDetailPresent,
            style: ["tableBody"],
          },
        ],
        [
          {
            colSpan: 6,
            margin: [3, 3, 0, 10],
            text: [
              {
                text: "NF.14 Member: ",
                bold: true,
              },
              {
                text: fatigue?.fcMem,
              },
            ],
          },
        ],
        [
          {
            colSpan: 6,
            margin: [3, 3, 0, 10],
            text: [
              {
                text: "NF.15 Member Detail Condition: ",
                bold: true,
              },
              {
                text: fatigue?.fcDescription,
              },
            ],
          },
        ],
        [
          {
            colSpan: 6,
            margin: [3, 3, 0, 10],
            text: [
              {
                text: "NF.16 Member Inspection Procedure: ",
                bold: true,
              },
              {
                text: fatigue?.detailInspProc,
              },
            ],
          },
        ],
      );
    }
  }
  return table;
}

function nstmFatigueTable() {
  return [
    {
      text: "NF.08\n Key",
      style: ["tableHeader"],
    },
    {
      text: "NF.09\n Location",
      style: ["tableHeader"],
    },
    {
      text: "NF.11\n Member Type",
      style: ["tableHeader"],
    },
    {
      text: "NF.12\n Member Detail",
      style: ["tableHeader"],
    },
    {
      text: "NF.10\n Member Stress Category",
      style: ["tableHeader"],
    },
    {
      text: "NF.13\n Detail Type",
      style: ["tableHeader"],
    },
  ];
}

function getFloodPlains(inspectionStore) {
  let floodPlains = clone(structure.inspectionsUnderwater);

  if (!floodPlains) {
    //remove null values from array(if any)
    floodPlains = floodPlains?.filter((a) => !!a);
  }

  for (let idx = 1; idx <= floodPlains?.length; idx++) {
    floodPlains[idx - 1].UNIQUE_KEY = idx;
    floodPlains[idx - 1].subUnit = inspectionStore.getSubStructure(floodPlains[idx - 1]?.strUnitKey)?.strUnitLabel;
  }
  return floodPlains || [];
}

function getPiers() {
  let structureUnits = clone(structure?.structureUnits) || [];
  return sortPiers(structureUnits?.filter((p) => p?.legacyStructureType == "P"));
}

function getPierNumber(pier) {
  if (pier?.strUnitLabel.includes("Pier")) {
    if (pier?.strUnitLabel.split(" ")[1].length == 1) {
      return "0" + pier?.strUnitLabel.split(" ")[1];
    } else {
      return pier?.strUnitLabel.split(" ")[1];
    }
  } else {
    return pier?.strUnitLabel.split("P")[1];
  }
}

function getPlaceholder(str) {
  let num = null;
  if (str?.includes("Pier")) {
    if (str?.split(" ")[1].length == 1) {
      num = "0" + str?.split(" ")[1];
    } else {
      num = str?.split(" ")[1];
    }
  } else {
    num = str?.split("P")[1];
  }

  return num?.charAt(0) === "0" ? Number.parseInt(num?.charAt(1)) : Number.parseInt(num);
}

function sortPiers(piers) {
  return piers?.sort((a, b) => {
    const aPlaceholder = getPlaceholder(a?.strUnitLabel);
    const bPlaceholder = getPlaceholder(b?.strUnitLabel);
    return aPlaceholder - bPlaceholder;
  });
}

export { setSubstructureDocDefinition };
